export const PROJECT_SCHEMA_VERSION = "1.0" as const;

export const PROJECT_SCENE_VARIANTS = {
  hook: ["count-up", "headline-slam", "question", "ticker"],
  explanation: ["chart", "constellation", "whiteboard", "annotated-text", "map"],
  comparison: ["split-screen", "ranked-bars", "metrics", "claim-swap"],
  process: ["horizontal-flow", "decision-tree", "hub-and-spoke", "state-rail"],
  "camera-journey": ["push-in", "pull-back", "device-dive", "scroll-story", "screen-carousel", "focus-carousel", "vertical-focus-carousel", "curved-focus-carousel", "filmstrip", "infinite-carousel", "elastic-carousel", "magnetic-carousel", "cover-flow", "stacked-card-carousel", "card-deck", "semicircle-carousel", "ferris-wheel-carousel"],
  conclusion: ["cta", "brand-lockup", "proof-summary", "thesis-reprise", "quiet-close"],
} as const;

export const PROJECT_TRANSITION_TYPES = [
  "cut",
  "dissolve",
  "slide",
  "fade-through",
  "type-match-cut",
  "whip-pan-cut",
  "zoom-through",
] as const;

/** The focused transition set exposed by the basic editor. */
export const CORE_TRANSITION_TYPES = ["cut", "dissolve", "slide", "fade-through"] as const;

export type ProjectSceneFamily = keyof typeof PROJECT_SCENE_VARIANTS;
export type ProjectSceneVariant = (typeof PROJECT_SCENE_VARIANTS)[ProjectSceneFamily][number];
export type ProjectSceneVariantFor<Family extends ProjectSceneFamily> = (typeof PROJECT_SCENE_VARIANTS)[Family][number];
export type ProjectTransitionType = (typeof PROJECT_TRANSITION_TYPES)[number];
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export type ProjectFormat = {
  width: number;
  height: number;
  fps: number;
};

export type ProjectTiming =
  | { mode: "auto" }
  | { mode: "fixed"; duration: number };

export type ProjectPalette = {
  background: string;
  surface: string;
  foreground: string;
  muted: string;
  accent: string;
  accentAlt: string;
};

export type ProjectTypography = {
  displayFamily: string;
  bodyFamily: string;
  scale: "compact" | "standard" | "large";
};

export type ProjectMotionTheme = {
  intensity: "reduced" | "standard" | "expressive";
  easing: "gentle" | "standard" | "snappy";
};

export type ProjectTheme = {
  palette: ProjectPalette;
  typography: ProjectTypography;
  density: "compact" | "standard" | "spacious";
  radius: "none" | "small" | "medium" | "large";
  motion: ProjectMotionTheme;
};

export type ProjectThemeOverride = {
  palette?: Partial<ProjectPalette>;
  typography?: Partial<ProjectTypography>;
  density?: ProjectTheme["density"];
  radius?: ProjectTheme["radius"];
  motion?: Partial<ProjectMotionTheme>;
};

export type ProjectScene = {
  [Family in ProjectSceneFamily]: {
    /** Stable editor and compiler identity. */
    id: string;
    family: Family;
    variant: ProjectSceneVariantFor<Family>;
    /** The complete visible window, including any transition overlap. */
    duration: number;
    /** Variant-specific, JSON-safe copy, data, assets, and configuration. */
    content: JsonObject;
    theme?: ProjectThemeOverride;
  }
}[ProjectSceneFamily];

export type ProjectTransition = {
  /** Stable identity for transitions that render their own visual layer. */
  id: string;
  fromSceneId: string;
  toSceneId: string;
  type: ProjectTransitionType;
  /** Overlap shared by the outgoing and incoming scene. Cuts use zero. */
  duration: number;
  config?: JsonObject;
};

export type ProjectDocument = {
  schemaVersion: typeof PROJECT_SCHEMA_VERSION;
  id: string;
  title: string;
  format: ProjectFormat;
  timing: ProjectTiming;
  theme: ProjectTheme;
  /** Array order is playback order. Start times and tracks are derived. */
  scenes: ProjectScene[];
  /** Exactly one explicit edge connects each adjacent pair of scenes. */
  transitions: ProjectTransition[];
};

export type ProjectTimelineScene = {
  id: string;
  start: number;
  duration: number;
  end: number;
  track: number;
};

export type ProjectTimeline = {
  duration: number;
  scenes: ProjectTimelineScene[];
};

export type ProjectValidationSeverity = "error" | "warning";

export type ProjectValidationCode =
  | "document_invalid"
  | "schema_version_unsupported"
  | "id_invalid"
  | "id_duplicate"
  | "format_invalid"
  | "timing_invalid"
  | "theme_invalid"
  | "scene_required"
  | "scene_family_invalid"
  | "scene_variant_invalid"
  | "scene_duration_invalid"
  | "scene_content_invalid"
  | "transition_count_invalid"
  | "transition_adjacency_invalid"
  | "transition_type_invalid"
  | "transition_duration_invalid"
  | "transition_config_invalid"
  | "fixed_duration_mismatch";

export type ProjectValidationIssue = {
  code: ProjectValidationCode;
  severity: ProjectValidationSeverity;
  path: string;
  message: string;
};

export type ProjectValidationResult = {
  valid: boolean;
  issues: ProjectValidationIssue[];
};

export const DEFAULT_PROJECT_THEME: ProjectTheme = {
  palette: {
    background: "#081019",
    surface: "#111c28",
    foreground: "#f8fafc",
    muted: "#94a3b8",
    accent: "#71f5a7",
    accentAlt: "#61a8ff",
  },
  typography: {
    displayFamily: "Inter",
    bodyFamily: "Inter",
    scale: "standard",
  },
  density: "standard",
  radius: "medium",
  motion: {
    intensity: "standard",
    easing: "standard",
  },
};

export const PROJECT_VALIDATION_RULES = [
  "Project, scene, and transition IDs are stable kebab-case identifiers and globally unique within their kind.",
  "A project contains at least one scene; scene array order is playback order.",
  "Every scene variant belongs to its declared semantic family.",
  "Every adjacent scene pair has exactly one explicit transition edge and no non-adjacent edge.",
  "A cut has zero duration; every other transition has positive duration shorter than both neighboring scenes.",
  "Scene starts are derived as previous start + previous duration - connecting transition duration.",
  "Auto timing uses the derived duration; fixed timing must equal it within one output frame.",
  "Scene content and transition configuration are JSON-safe so UI edits, persistence, and LLM patches share one format.",
] as const;

const ID_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFinitePositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isJsonValue(value: unknown, seen = new Set<object>()): value is JsonValue {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  const valid = Array.isArray(value)
    ? value.every((item) => isJsonValue(item, seen))
    : Object.values(value).every((item) => isJsonValue(item, seen));
  seen.delete(value);
  return valid;
}

function addIssue(
  issues: ProjectValidationIssue[],
  code: ProjectValidationCode,
  path: string,
  message: string,
  severity: ProjectValidationSeverity = "error",
): void {
  issues.push({ code, severity, path, message });
}

export function deriveProjectTimeline(project: Pick<ProjectDocument, "scenes" | "transitions">): ProjectTimeline {
  const transitions = new Map(project.transitions.map((transition) => [
    `${transition.fromSceneId}\u0000${transition.toSceneId}`,
    transition,
  ]));
  let start = 0;
  const scenes = project.scenes.map((scene, index): ProjectTimelineScene => {
    if (index > 0) {
      const previous = project.scenes[index - 1]!;
      const edge = transitions.get(`${previous.id}\u0000${scene.id}`);
      start += previous.duration - (edge?.duration ?? 0);
    }
    return { id: scene.id, start, duration: scene.duration, end: start + scene.duration, track: index + 1 };
  });
  return { duration: scenes.at(-1)?.end ?? 0, scenes };
}

export function validateProjectDocument(input: unknown): ProjectValidationResult {
  const issues: ProjectValidationIssue[] = [];
  if (!isRecord(input)) {
    addIssue(issues, "document_invalid", "$", "Project document must be an object.");
    return { valid: false, issues };
  }

  if (input.schemaVersion !== PROJECT_SCHEMA_VERSION) addIssue(issues, "schema_version_unsupported", "schemaVersion", `Expected schema version ${PROJECT_SCHEMA_VERSION}.`);
  if (typeof input.id !== "string" || !ID_PATTERN.test(input.id)) addIssue(issues, "id_invalid", "id", "Project ID must be a stable kebab-case identifier.");
  if (typeof input.title !== "string" || !input.title.trim()) addIssue(issues, "document_invalid", "title", "Project title is required.");

  if (!isRecord(input.format) || !Number.isInteger(input.format.width) || !Number.isInteger(input.format.height) || !Number.isInteger(input.format.fps) || !isFinitePositive(input.format.width) || !isFinitePositive(input.format.height) || !isFinitePositive(input.format.fps) || input.format.fps > 120) {
    addIssue(issues, "format_invalid", "format", "Width, height, and fps must be positive integers; fps cannot exceed 120.");
  }

  if (!isRecord(input.timing) || (input.timing.mode !== "auto" && input.timing.mode !== "fixed") || (input.timing.mode === "fixed" && !isFinitePositive(input.timing.duration))) {
    addIssue(issues, "timing_invalid", "timing", "Timing must be auto, or fixed with a positive duration.");
  }

  const theme = input.theme;
  if (!isRecord(theme) || !isRecord(theme.palette) || !isRecord(theme.typography) || !isRecord(theme.motion)) {
    addIssue(issues, "theme_invalid", "theme", "Theme must define palette, typography, density, radius, and motion tokens.");
  } else {
    const palette = theme.palette;
    const typography = theme.typography;
    const motion = theme.motion;
    const paletteKeys = ["background", "surface", "foreground", "muted", "accent", "accentAlt"];
    const paletteValid = paletteKeys.every((key) => typeof palette[key] === "string" && palette[key] !== "");
    const typographyValid = typeof typography.displayFamily === "string" && typeof typography.bodyFamily === "string" && ["compact", "standard", "large"].includes(String(typography.scale));
    const motionValid = ["reduced", "standard", "expressive"].includes(String(motion.intensity)) && ["gentle", "standard", "snappy"].includes(String(motion.easing));
    if (!paletteValid || !typographyValid || !motionValid || !["compact", "standard", "spacious"].includes(String(theme.density)) || !["none", "small", "medium", "large"].includes(String(theme.radius))) {
      addIssue(issues, "theme_invalid", "theme", "Theme contains missing or unsupported token values.");
    }
  }

  if (!Array.isArray(input.scenes) || input.scenes.length === 0) {
    addIssue(issues, "scene_required", "scenes", "At least one scene is required.");
  }
  if (!Array.isArray(input.transitions)) addIssue(issues, "transition_count_invalid", "transitions", "Transitions must be an array.");
  if (!Array.isArray(input.scenes) || !Array.isArray(input.transitions)) return { valid: false, issues };
  const scenes = input.scenes;
  const transitions = input.transitions;

  const sceneIds = new Set<string>();
  scenes.forEach((value, index) => {
    const path = `scenes[${index}]`;
    if (!isRecord(value)) {
      addIssue(issues, "document_invalid", path, "Scene must be an object.");
      return;
    }
    if (typeof value.id !== "string" || !ID_PATTERN.test(value.id)) addIssue(issues, "id_invalid", `${path}.id`, "Scene ID must be kebab-case.");
    else if (sceneIds.has(value.id)) addIssue(issues, "id_duplicate", `${path}.id`, `Duplicate scene ID "${value.id}".`);
    else sceneIds.add(value.id);
    if (typeof value.family !== "string" || !(value.family in PROJECT_SCENE_VARIANTS)) {
      addIssue(issues, "scene_family_invalid", `${path}.family`, `Unknown scene family "${String(value.family)}".`);
    } else if (typeof value.variant !== "string" || !(PROJECT_SCENE_VARIANTS[value.family as ProjectSceneFamily] as readonly string[]).includes(value.variant)) {
      addIssue(issues, "scene_variant_invalid", `${path}.variant`, `Variant "${String(value.variant)}" does not belong to ${value.family}.`);
    }
    if (!isFinitePositive(value.duration)) addIssue(issues, "scene_duration_invalid", `${path}.duration`, "Scene duration must be positive.");
    if (!isRecord(value.content) || !isJsonValue(value.content)) addIssue(issues, "scene_content_invalid", `${path}.content`, "Scene content must be a JSON-safe object.");
    if (value.theme !== undefined && (!isRecord(value.theme) || !isJsonValue(value.theme))) addIssue(issues, "theme_invalid", `${path}.theme`, "Scene theme override must be a JSON-safe object.");
  });

  const expectedTransitionCount = Math.max(0, scenes.length - 1);
  if (transitions.length !== expectedTransitionCount) addIssue(issues, "transition_count_invalid", "transitions", `Expected ${expectedTransitionCount} transition edge(s) for ${scenes.length} scene(s).`);
  const transitionIds = new Set<string>();
  transitions.forEach((value, index) => {
    const path = `transitions[${index}]`;
    if (!isRecord(value)) {
      addIssue(issues, "document_invalid", path, "Transition must be an object.");
      return;
    }
    if (typeof value.id !== "string" || !ID_PATTERN.test(value.id)) addIssue(issues, "id_invalid", `${path}.id`, "Transition ID must be kebab-case.");
    else if (transitionIds.has(value.id)) addIssue(issues, "id_duplicate", `${path}.id`, `Duplicate transition ID "${value.id}".`);
    else transitionIds.add(value.id);
    const expectedFrom = scenes[index];
    const expectedTo = scenes[index + 1];
    if (!isRecord(expectedFrom) || !isRecord(expectedTo) || value.fromSceneId !== expectedFrom.id || value.toSceneId !== expectedTo.id) {
      addIssue(issues, "transition_adjacency_invalid", path, `Transition ${index + 1} must connect the adjacent scenes at positions ${index + 1} and ${index + 2}.`);
    }
    if (typeof value.type !== "string" || !(PROJECT_TRANSITION_TYPES as readonly string[]).includes(value.type)) addIssue(issues, "transition_type_invalid", `${path}.type`, `Unknown transition type "${String(value.type)}".`);
    const transitionDuration = value.duration;
    const durationValid = typeof transitionDuration === "number" && Number.isFinite(transitionDuration) && transitionDuration >= 0;
    if (!durationValid || (value.type === "cut" && transitionDuration !== 0) || (value.type !== "cut" && typeof transitionDuration === "number" && transitionDuration <= 0)) {
      addIssue(issues, "transition_duration_invalid", `${path}.duration`, "Cuts require zero duration; other transitions require a positive duration.");
    } else if (typeof transitionDuration === "number" && isRecord(expectedFrom) && isRecord(expectedTo) && isFinitePositive(expectedFrom.duration) && isFinitePositive(expectedTo.duration) && transitionDuration >= Math.min(expectedFrom.duration, expectedTo.duration)) {
      addIssue(issues, "transition_duration_invalid", `${path}.duration`, "Transition duration must be shorter than both neighboring scenes.");
    }
    if (value.config !== undefined && (!isRecord(value.config) || !isJsonValue(value.config))) addIssue(issues, "transition_config_invalid", `${path}.config`, "Transition configuration must be a JSON-safe object.");
  });

  if (!issues.some((issue) => issue.severity === "error") && isRecord(input.timing) && input.timing.mode === "fixed" && typeof input.timing.duration === "number" && isRecord(input.format) && typeof input.format.fps === "number") {
    const project = input as unknown as ProjectDocument;
    const derived = deriveProjectTimeline(project).duration;
    if (Math.abs(input.timing.duration - derived) > 1 / input.format.fps) addIssue(issues, "fixed_duration_mismatch", "timing.duration", `Fixed duration ${input.timing.duration}s does not match the derived ${derived}s timeline within one frame.`);
  }

  return { valid: !issues.some((issue) => issue.severity === "error"), issues };
}

export function assertValidProjectDocument(input: unknown): asserts input is ProjectDocument {
  const result = validateProjectDocument(input);
  if (!result.valid) throw new Error(result.issues.map((issue) => `${issue.path}: ${issue.message}`).join("\n"));
}
